import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { IVideo } from '@gray/domain/models/video/video.model';
import { ILiveStream } from '@gray/domain/models/live-stream/live-stream.model';
import { ELiveStreamStatus } from '@gray/domain/enums/live-stream-status.enum';
import { CoachStudentContentService } from '../../../../core/services/coach-student-content.service';
import { IContentItem } from '../../../../core/models/content-item.model';

/**
 * Watching a piece of content before handing it to a student. The detail is fetched through the
 * student, so what plays here is what the student would get — including the proxied stream URL the
 * web player needs.
 *
 * A live stream is only playable once it has finished and its recording exists; before that the
 * dialog says when it airs rather than showing an empty player.
 */
@Component({
  selector: 'coach-content-preview-modal',
  templateUrl: './content-preview-modal.component.html',
  styleUrl: './content-preview-modal.component.scss',
})
export class ContentPreviewModalComponent implements OnInit, OnDestroy {
  @Input() studentId!: number;
  @Input() periodId?: number | null;
  @Input() item!: IContentItem;
  @Input() isAssigned = false;

  /** Raised when the coach decides to assign what they are watching. */
  @Output() assignRequested = new EventEmitter<IContentItem>();

  video?: IVideo;
  liveStream?: ILiveStream;

  isLoading = false;
  hasError = false;

  streamStatus = ELiveStreamStatus;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private contentService: CoachStudentContentService,
  ) {
  }

  ngOnInit(): void {
    this.getDetail();
  }

  getDetail() {
    this.isLoading = true;
    this.hasError = false;

    const request = this.item.type === 'video'
      ? this.contentService.getVideo(this.studentId, this.item.id, this.periodId)
      : this.contentService.getLiveStream(this.studentId, this.item.id, this.periodId);

    request
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe({
        next: r => {
          if (this.item.type === 'video') {
            this.video = r.data as IVideo;
            return;
          }

          this.liveStream = r.data as ILiveStream;
          this.video = this.liveStream?.video;
        },
        error: () => this.hasError = true,
      });
  }

  title(): string {
    return this.video?.title ?? this.liveStream?.title ?? this.item.title;
  }

  description(): string | null {
    return this.video?.description ?? this.liveStream?.description ?? null;
  }

  /** What the player streams; empty until the detail with its source has arrived. */
  playerSrc(): string | null {
    return this.video?.source?.resourceUrl ?? null;
  }

  /** A stream still to air has nothing to play, and says so. */
  isUpcomingStream(): boolean {
    return !!this.liveStream && this.liveStream.status !== ELiveStreamStatus.Finished;
  }

  assign() {
    this.assignRequested.emit(this.item);
    this.modal.dismiss();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
